import { AirParams, FarePriceErrorCode, FarePriceResponse, GatewayParam, PriceResponseV2, PricesResponseV2, SingleFareOptions } from '@ssc-booking/api-contracts';
import * as Amount from '@ssc-booking/api-contracts/src/amount';
import * as AirClass from '@ssc-booking/api-contracts/src/air-class';
import { FareCode } from "@silversea-ssc/web-fares-fp"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import * as Separated from "fp-ts/Separated"
import * as ORD from "fp-ts/Ord"
import { brandFromPredicate, NonEmptyString } from "@ssc-booking/functional-support"
import { Int, TypeOf } from "io-ts"
import { availablePrices } from "../../../../_common/price-response"
import { FeasibleAirFares } from "./feasible-air-fares"

export const FareCardKey = brandFromPredicate(NonEmptyString.strictType, F.constTrue, "FareCardKey")
export type FareCardKey = TypeOf<typeof FareCardKey>
export const FareCardIndex = brandFromPredicate(Int, F.constTrue, "FareCardIndex")
export type FareCardIndex = TypeOf<typeof FareCardIndex>
export type VsEligibility = "eligible" | "non-eligible"

export type FareCardStatus = FareCard["status"]
export type FareCard =
    | {
          key: FareCardKey
          index: FareCardIndex
          fare: FareCode
          options: SingleFareOptions
          feasibleAirFares: FeasibleAirFares
          status: "unknown" | FarePriceErrorCode
          vsEligibility: VsEligibility
          air: AirParams
          fareLabel: string
          suitePrices: PricesResponseV2["prices"]
      }
    | {
          key: FareCardKey
          index: FareCardIndex
          fare: FareCode
          options: SingleFareOptions
          feasibleAirFares: FeasibleAirFares
          status: "good"
          vsEligibility: VsEligibility
          air: AirParams
          response: FarePriceResponse
          fareLabel: string
          suitePrices: PricesResponseV2["prices"]
      }

const hasStatus =
    <Status extends FareCardStatus>(status: Status) =>
    (x: FareCard): x is FareCard & { status: Status } =>
        x.status === status

export const FareCard = {
    asGood: (
        response: FarePriceResponse,
        fareCard: FareCard,
        suitePrices: PricesResponseV2["prices"] = [],
    ): FareCard => ({ ...fareCard, status: "good", response, suitePrices }),
    updateStatusWithSuitePrices: (response: PricesResponseV2, fareCard: FareCard): FareCard =>
        F.pipe(
            availablePrices(response.prices),
            ROA.sort(bySuitePriceTotal),
            ROA.head,
            O.match(
                () => FareCard.asError("noAvailablePrice", fareCard, response.prices),
                (farePriceResponse) => FareCard.asGood(farePriceResponse, fareCard, response.prices),
            ),
        ),
    asError: (
        code: FarePriceErrorCode,
        fareCard: FareCard,
        suitePrices: PricesResponseV2["prices"] = [],
    ): FareCard => ({ ...fareCard, status: code, suitePrices }),
    hasStatus,
    hasFlights: (fareCard: FareCard) => FeasibleAirFares.hasFlights(fareCard.feasibleAirFares),
    updateGateway: (gateway: string) => (fareCard: FareCard) =>
        F.pipe(
            fareCard.air,
            AirParams.updateGateway(GatewayParam.userSelected(gateway)),
            O.fromPredicate(
                (newAir) =>
                    !AirParams.eq.equals(fareCard.air, newAir) &&
                    fareCard.options.gateways.some((x) => x.code === gateway),
            ),
            O.map((air): FareCard => ({ ...fareCard, air })),
        ),
    updateAirClass: (airClass: AirClass.AirClass) => (fareCard: FareCard) =>
        F.pipe(
            fareCard.air,
            AirParams.updateAirClass(airClass),
            O.fromPredicate(
                (newAir) =>
                    !AirParams.eq.equals(fareCard.air, newAir) &&
                    FeasibleAirFares.includes(airClass)(fareCard.feasibleAirFares),
            ),
            O.map((air): FareCard => ({ ...fareCard, air })),
        ),
    updatePriceBySuiteCategory: (suiteCategory: string, fareCard: FareCard) =>
        F.pipe(
            availablePrices(fareCard.suitePrices),
            ROA.findFirst((x) => x.suiteCategory === suiteCategory),
            O.map((priceResponse) => FareCard.asGood(priceResponse, fareCard, fareCard.suitePrices)),
            O.getOrElse(() => FareCard.asError("noAvailablePrice", fareCard, fareCard.suitePrices)),
        ),
    cheapest: (fareCards: readonly FareCard[]) =>
        F.pipe(
            // keep new line
            fareCards,
            ROA.filter(hasStatus("good")),
            ROA.sort(byCheapestResponsePrice),
            ROA.head,
        ),
    asUnknown: (fareCard: FareCard): FareCard => ({ ...fareCard, status: "unknown" }),
}

const bySuitePriceTotal = F.pipe(Amount.cheapest, ORD.contramap(PriceResponseV2.lensFromPath(["price", "total"]).get))

const byCheapestResponsePrice = F.pipe(
    // keep new line
    Amount.cheapest,
    ORD.contramap((x: FareCard & { status: "good" }) => x.response.price.total),
)
